/**
 * A small async-load hook shared by the detail pages. It runs an async loader on mount (and whenever
 * the caller's `deps` change), tracks loading/error/loaded, guards against setting state after the
 * effect is torn down (avoids a stale response from an earlier `deps` clobbering a newer one), and
 * classifies a `401` {@link ApiError} as an auth error so the UI can prompt re-authentication.
 *
 * The loader is invoked from the effect keyed on `deps`, so — exactly like `useEffect` — the `deps`
 * array must list every value the loader closes over that should trigger a reload (e.g. the API
 * client, a path id, or a selected time range).
 */

import { useEffect, useState, type DependencyList } from 'react';

import { ApiError } from '../api/client';

/** The lifecycle of a single fetched resource. */
export type Resource<T> =
  | { status: 'loading' }
  | { status: 'error'; message: string; isAuthError: boolean }
  | { status: 'loaded'; data: T };

export function useResource<T>(load: () => Promise<T>, deps: DependencyList): Resource<T> {
  const [resource, setResource] = useState<Resource<T>>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    setResource({ status: 'loading' });
    load()
      .then((data) => {
        if (active) setResource({ status: 'loaded', data });
      })
      .catch((error: unknown) => {
        if (!active) return;
        const message = error instanceof Error ? error.message : 'Request failed';
        const isAuthError = error instanceof ApiError && error.isAuthError;
        setResource({ status: 'error', message, isAuthError });
      });
    return () => {
      active = false;
    };
    // The caller owns the dependency list (it knows what the loader closes over).
  }, deps); // eslint-disable-line react-hooks/exhaustive-deps

  return resource;
}
